import React, { useState, useEffect } from "react";
import {
  Modal,
  Badge,
  Button,
  Table,
  Alert,
  Text,
  Group,
  Stack,
  ScrollArea,
  Tabs,
  Checkbox,
  Loader,
} from "@mantine/core";
import {
  AlertCircle,
  InfoCircle,
  Check,
  Refresh,
} from "tabler-icons-react";
import {
  SyncDiffResult,
  OptionMetadataValue,
} from "../../hooks/useFormOptionsSetBuilder";
import { ScrollableTabs } from "../shared";

export interface OptionSyncDiffModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (overrideZeroGuard?: boolean) => Promise<void>;
  diff: SyncDiffResult | null;
  optionsSetName: string;
  loading: boolean;
}

// Mantine v4 Tabs.Tab typing helper for JSX compatibility
const TabItem = Tabs.Tab as unknown as React.ComponentType<{
  label: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  tabKey?: string;
  color?: string;
  [key: string]: any;
}>;

export default function OptionSyncDiffModal({
  opened,
  onClose,
  onConfirm,
  diff,
  optionsSetName,
  loading,
}: OptionSyncDiffModalProps) {
  const [overrideZeroGuard, setOverrideZeroGuard] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);

  // Reset override guard and set active tab on modal open or diff change
  useEffect(() => {
    if (opened) {
      setOverrideZeroGuard(false);
      if (diff) {
        if (diff.toAdd.length > 0) {
          setActiveTab(0);
        } else if (diff.toUpdate.length > 0) {
          setActiveTab(1);
        } else if (diff.toPrune.length > 0) {
          setActiveTab(2);
        } else {
          setActiveTab(3);
        }
      }
    }
  }, [opened, diff]);

  const handleConfirm = async () => {
    if (!diff) return;
    if (diff.isZeroSubmissionBlocked && !overrideZeroGuard) return;
    await onConfirm(overrideZeroGuard);
  };

  const renderMetadataDiff = (
    prevMeta: Record<string, OptionMetadataValue> | null | undefined,
    nextMeta: Record<string, OptionMetadataValue> | null | undefined
  ) => {
    const prev = prevMeta || {};
    const next = nextMeta || {};
    const allKeys = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)]));

    const changedBadges: React.ReactNode[] = [];
    allKeys.forEach((key) => {
      const pVal = prev[key]?.value;
      const nVal = next[key]?.value;
      const label = next[key]?.label || prev[key]?.label || key;
      if (pVal !== nVal) {
        changedBadges.push(
          <Badge key={key} size="xs" color="blue" variant="light" style={{ textTransform: "none" }}>
            {label}: {pVal ?? "none"} → {nVal ?? "none"}
          </Badge>
        );
      }
    });

    if (changedBadges.length === 0) {
      return <Text size="xs" color="dimmed">Metadata updated</Text>;
    }

    return (
      <Group spacing={4}>
        {changedBadges}
      </Group>
    );
  };

  const isConfirmDisabled =
    loading ||
    !diff ||
    (diff.isZeroSubmissionBlocked && !overrideZeroGuard);

  const confirmButtonColor =
    diff?.isZeroSubmissionBlocked && overrideZeroGuard
      ? "red"
      : "cap-navy";

  const confirmButtonText =
    diff?.isZeroSubmissionBlocked && overrideZeroGuard
      ? "Force Sync (Prune All)"
      : "Apply Sync";

  return (
    <Modal
      opened={opened}
      onClose={loading ? () => {} : onClose}
      title={
        <Group spacing="xs">
          <Refresh size={20} />
          <Text size="lg" weight={600}>
            Synchronize Option Set: "{optionsSetName}"
          </Text>
        </Group>
      }
      size="xl"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      {loading && !diff ? (
        <Group position="center" my="xl" direction="column" align="center">
          <Loader color="cap-navy" size="md" />
          <Text size="sm" color="dimmed">
            Calculating synchronization differences...
          </Text>
        </Group>
      ) : !diff ? (
        <Alert color="gray" icon={<InfoCircle size={16} />}>
          No synchronization data available.
        </Alert>
      ) : (
        <Stack spacing="md">
          {/* Summary Metric Badges */}
          <Group spacing="xs" style={{ flexWrap: "wrap" }}>
            <Badge color="green" size="md" variant="filled">
              +{diff.toAdd.length} to Add
            </Badge>
            <Badge color="blue" size="md" variant="filled">
              {diff.toUpdate.length} to Update
            </Badge>
            <Badge color="red" size="md" variant="filled">
              -{diff.toPrune.length} to Prune
            </Badge>
            <Badge color="gray" size="md" variant="light">
              {diff.unchanged.length} Unchanged
            </Badge>
            <Badge color="indigo" size="md" variant="outline">
              {diff.authoritativeOptions.length} Authoritative Options
            </Badge>
            <Badge color="cyan" size="md" variant="outline">
              {diff.totalSubmissions} Submissions Queried
            </Badge>
          </Group>

          {/* Critical Zero-Submission Guard Warning */}
          {diff.isZeroSubmissionBlocked && (
            <Alert
              color="red"
              icon={<AlertCircle size={20} />}
              title="Critical Warning: Zero Submissions Guard Triggered"
            >
              <Stack spacing="xs">
                <Text size="sm">
                  The query returned <b>0 submissions</b> for this linked form template, but this option set currently contains{" "}
                  <b>{diff.toPrune.length} active {diff.toPrune.length === 1 ? "option" : "options"}</b>.
                  Applying this synchronization will permanently remove all existing options.
                </Text>
                <Checkbox
                  checked={overrideZeroGuard}
                  onChange={(e) => setOverrideZeroGuard(e.currentTarget.checked)}
                  color="red"
                  label="I understand this will prune all options and want to proceed anyway"
                  size="sm"
                  data-testid="zero-guard-override-checkbox"
                />
              </Stack>
            </Alert>
          )}

          {/* Prune Warning (when not zero-blocked) */}
          {diff.toPrune.length > 0 && !diff.isZeroSubmissionBlocked && (
            <Alert
              color="orange"
              icon={<AlertCircle size={20} />}
              title="Warning: Obsolete Options Will Be Pruned"
            >
              <Text size="sm">
                <b>{diff.toPrune.length}</b> {diff.toPrune.length === 1 ? "option" : "options"} currently in this option set no longer exist in the authoritative form submissions and will be permanently removed upon sync.
              </Text>
            </Alert>
          )}

          {/* No Changes Info */}
          {diff.toAdd.length === 0 &&
            diff.toUpdate.length === 0 &&
            diff.toPrune.length === 0 && (
              <Alert color="teal" icon={<Check size={16} />}>
                This option set is already fully up to date with all form submissions. Applying sync will refresh the synchronization timestamp.
              </Alert>
            )}

          {/* Breakdown Tabs */}
          <ScrollableTabs active={activeTab} onTabChange={setActiveTab} color="cap-navy">
            {/* Tab 0: Additions */}
            <TabItem label={`Additions (${diff.toAdd.length})`} tabKey="additions">
              <ScrollArea style={{ maxHeight: 320 }} mt="xs">
                {diff.toAdd.length === 0 ? (
                  <Text color="dimmed" size="sm" py="md" align="center">
                    No new options to add.
                  </Text>
                ) : (
                  <Table striped highlightOnHover verticalSpacing="xs">
                    <thead>
                      <tr>
                        <th style={{ width: "35%" }}>Value</th>
                        <th style={{ width: "35%" }}>Label</th>
                        <th style={{ width: "30%" }}>Metadata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diff.toAdd.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <Text size="sm" weight={600} color="green">
                              {item.value}
                            </Text>
                          </td>
                          <td>
                            <Text size="sm">{item.label}</Text>
                          </td>
                          <td>
                            {item.metadata && Object.keys(item.metadata).length > 0 ? (
                              <Group spacing={4}>
                                {Object.entries(item.metadata).map(([k, meta]) => (
                                  <Badge
                                    key={k}
                                    size="xs"
                                    variant="outline"
                                    color="green"
                                    style={{ textTransform: "none" }}
                                  >
                                    {meta.label || k}: {meta.value}
                                  </Badge>
                                ))}
                              </Group>
                            ) : (
                              <Text size="xs" color="dimmed">
                                —
                              </Text>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </ScrollArea>
            </TabItem>

            {/* Tab 1: Updates */}
            <TabItem label={`Updates (${diff.toUpdate.length})`} tabKey="updates">
              <ScrollArea style={{ maxHeight: 320 }} mt="xs">
                {diff.toUpdate.length === 0 ? (
                  <Text color="dimmed" size="sm" py="md" align="center">
                    No existing options need updates.
                  </Text>
                ) : (
                  <Table striped highlightOnHover verticalSpacing="xs">
                    <thead>
                      <tr>
                        <th style={{ width: "30%" }}>Value</th>
                        <th style={{ width: "35%" }}>Label (Previous → New)</th>
                        <th style={{ width: "35%" }}>Metadata Changes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diff.toUpdate.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <Text size="sm" weight={600} color="blue">
                              {item.value}
                            </Text>
                          </td>
                          <td>
                            {item.labelChanged ? (
                              <div>
                                <Text
                                  size="xs"
                                  color="dimmed"
                                  style={{ textDecoration: "line-through" }}
                                >
                                  {item.previous?.label || "—"}
                                </Text>
                                <Text size="sm" weight={500} color="blue">
                                  {item.label}
                                </Text>
                              </div>
                            ) : (
                              <Text size="sm">{item.label}</Text>
                            )}
                          </td>
                          <td>
                            {item.metadataChanged ? (
                              renderMetadataDiff(item.previous?.metadata, item.metadata)
                            ) : (
                              <Text size="xs" color="dimmed">
                                No metadata changes
                              </Text>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </ScrollArea>
            </TabItem>

            {/* Tab 2: Pruned */}
            <TabItem label={`Pruned (${diff.toPrune.length})`} tabKey="pruned">
              <ScrollArea style={{ maxHeight: 320 }} mt="xs">
                {diff.toPrune.length === 0 ? (
                  <Text color="dimmed" size="sm" py="md" align="center">
                    No options will be pruned.
                  </Text>
                ) : (
                  <Table striped highlightOnHover verticalSpacing="xs">
                    <thead>
                      <tr>
                        <th style={{ width: "35%" }}>Value</th>
                        <th style={{ width: "35%" }}>Label</th>
                        <th style={{ width: "30%" }}>Metadata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diff.toPrune.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <Text size="sm" weight={600} color="red">
                              {item.value}
                            </Text>
                          </td>
                          <td>
                            <Text
                              size="sm"
                              color="red"
                              style={{ textDecoration: "line-through" }}
                            >
                              {item.label}
                            </Text>
                          </td>
                          <td>
                            {item.metadata && Object.keys(item.metadata).length > 0 ? (
                              <Group spacing={4}>
                                {Object.entries(item.metadata).map(([k, meta]) => (
                                  <Badge
                                    key={k}
                                    size="xs"
                                    variant="outline"
                                    color="red"
                                    style={{ textTransform: "none" }}
                                  >
                                    {meta.label || k}: {meta.value}
                                  </Badge>
                                ))}
                              </Group>
                            ) : (
                              <Text size="xs" color="dimmed">
                                —
                              </Text>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </ScrollArea>
            </TabItem>

            {/* Tab 3: Unchanged */}
            <TabItem label={`Unchanged (${diff.unchanged.length})`} tabKey="unchanged">
              <ScrollArea style={{ maxHeight: 320 }} mt="xs">
                {diff.unchanged.length === 0 ? (
                  <Text color="dimmed" size="sm" py="md" align="center">
                    No unchanged options.
                  </Text>
                ) : (
                  <Table striped highlightOnHover verticalSpacing="xs">
                    <thead>
                      <tr>
                        <th style={{ width: "40%" }}>Value</th>
                        <th style={{ width: "60%" }}>Label</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diff.unchanged.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <Text size="sm" weight={500}>
                              {item.value}
                            </Text>
                          </td>
                          <td>
                            <Text size="sm" color="dimmed">
                              {item.label}
                            </Text>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </ScrollArea>
            </TabItem>
          </ScrollableTabs>

          {/* Modal Actions */}
          <Group position="apart" mt="lg">
            <Button variant="default" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              color={confirmButtonColor}
              onClick={handleConfirm}
              loading={loading}
              disabled={isConfirmDisabled}
              data-testid="apply-sync-button"
            >
              {confirmButtonText}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
